import React from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import {
  AddAddendumRadioGroupOptions,
  GrandfatheredUserValue,
} from 'config/automatic-rollovers'
import * as Yup from 'yup'
import {
  RadioGroup,
  ScrollErrorForm,
  ExternalLink,
  Notification,
} from 'components'
import { FlowActions } from '../components'
import { map } from 'lodash'
import {
  isNotificationsAddonEnabled,
  isGfBDAgreementsEnabled,
} from 'config/portal'
import { Variant as NotificationVariant } from 'components/Notification'
import * as Types from 'types'

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  currentUser: Types.user.isRequired,
}
const addAddendumValues = map(AddAddendumRadioGroupOptions, 'value')
const addNotificationsAddendumValues = map(
  AddAddendumRadioGroupOptions,
  'value'
)
const NOTIFICATIONS_FEE_SCHEDULE_URL =
  process.env.REACT_APP_NOTIFICATIONS_FEE_SCHEDULE_URL
const BD_FEE_SCHEDULE_URL = process.env.REACT_APP_BD_FEE_SCHEDULE_URL

function AddAddendumForm({ onSubmit, initialValues, currentUser }) {
  const isGrandfatheredUser =
    currentUser?.grandfatheredUser === GrandfatheredUserValue &&
    isGfBDAgreementsEnabled()
  const bdAgreementType = isGrandfatheredUser ? 'agreement' : 'addendum'
  const bdAgreementOptions = isGrandfatheredUser
    ? 'addendums or services agreements'
    : 'addendums'
  const bdAgreementOption = isGrandfatheredUser
    ? 'addendum or services agreement'
    : 'addendum'
  const bdRadioGroupOptions = AddAddendumRadioGroupOptions.map((option) =>
    option.value === 'yes'
      ? {
          ...option,
          key: `Yes, I'd like to sign the ${bdAgreementType}`,
        }
      : option
  )
  const addAddendumSchema = Yup.object({
    addAddendum: Yup.string()
      .oneOf(addAddendumValues, 'Select an option to continue')
      .required('Select an option to continue'),
    addNotificationsAddendum: Yup.lazy(() => {
      if (isNotificationsAddonEnabled())
        return Yup.string()
          .oneOf(addNotificationsAddendumValues, 'Select an option to continue')
          .required('Select an option to continue')
      return Yup.string().nullable()
    }),
  })
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={addAddendumSchema}
    >
      {() => {
        return (
          <ScrollErrorForm>
            <div className="form-section--with-top-border">
              <p className="description">
                You can sign optional {bdAgreementOptions} to your automatic
                rollover agreement to access additional products in the
                retirement services platform.
              </p>
              <Notification type={NotificationVariant.WARNING}>
                <p className="text-title--small">
                  If you don’t intend to use these additional services, you
                  don’t need to sign an {bdAgreementOption}.
                </p>
              </Notification>
              {isNotificationsAddonEnabled() && (
                <>
                  <h3>Notification services</h3>
                  <p>
                    Notification services make it easy for you to provide notice
                    to plan participants of an upcoming automatic distribution
                    to an IRA, unless a distribution election is made. This
                    product is an additional service associated with automatic
                    rollover IRA services.{' '}
                    <ExternalLink
                      link={NOTIFICATIONS_FEE_SCHEDULE_URL}
                      className="link-text"
                      aria-label="Bd fee schedule"
                    >
                      Download fee schedule
                    </ExternalLink>
                  </p>
                  <p className="signing-options">
                    Would you like to sign the addendum for notification
                    services?
                  </p>
                  <RadioGroup
                    options={AddAddendumRadioGroupOptions}
                    name="addNotificationsAddendum"
                    label={false}
                    className="radio-container-input"
                  />
                </>
              )}
              <h3>Benefit distributions</h3>
              <p className="bd-addendum">
                Benefit distributions make it easy to initiate and manage
                distributions elected by your participants.{' '}
                {!isGrandfatheredUser &&
                  'This product is an additional service associated with automatic rollover IRA services.'}{' '}
                <ExternalLink
                  link={BD_FEE_SCHEDULE_URL}
                  className="link-text"
                  aria-label="Bd fee schedule"
                >
                  Download fee schedule
                </ExternalLink>
              </p>
              <p className="signing-options">
                Would you like to sign the {bdAgreementType} for benefit
                distributions?
              </p>
              <RadioGroup
                options={bdRadioGroupOptions}
                name="addAddendum"
                label={false}
                className="radio-container-input"
              />
            </div>
            <FlowActions submitContent="Next" />
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

AddAddendumForm.propTypes = propTypes

export default AddAddendumForm
